/*
 * 模块: OverviewTab
 * 职责: 渲染指标概览标签页（定义、公式、范围建议）
 * 依赖: antd组件、类型定义
 * 导出: OverviewTab组件
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Card, Space, Alert, Timeline, Tag, Typography } from 'antd';
import { BookOutlined, CalculatorOutlined, LineChartOutlined } from '@ant-design/icons';
import type { MetricDefinition } from '../../../types';

const { Text, Paragraph } = Typography;

interface OverviewTabProps {
  definition: MetricDefinition;
}

const buildActionGuideData = (def: MetricDefinition) => {
  // 防御性检查：确保range和action_guide存在且有效
  if (!def.range?.high || !def.range?.medium || !def.range?.low) {
    return [];
  }
  
  return [
    { 
      key: 'high',
      level: '高', 
      range: `${def.range.high[0] ?? 0}-${def.range.high[1] ?? 0}${def.unit || ''}`, 
      desc: def.range.high[2] || '高水平', 
      action: def.action_guide?.high || '暂无建议',
      color: '#52c41a'
    },
    { 
      key: 'medium',
      level: '中', 
      range: `${def.range.medium[0] ?? 0}-${def.range.medium[1] ?? 0}${def.unit || ''}`, 
      desc: def.range.medium[2] || '中等水平', 
      action: def.action_guide?.medium || '暂无建议',
      color: '#fa8c16'
    },
    { 
      key: 'low',
      level: '低', 
      range: `${def.range.low[0] ?? 0}-${def.range.low[1] ?? 0}${def.unit || ''}`, 
      desc: def.range.low[2] || '低水平', 
      action: def.action_guide?.low || '暂无建议',
      color: '#ff4d4f'
    },
  ];
};

export const OverviewTab: React.FC<OverviewTabProps> = ({ definition }) => {
  const actionGuideData = buildActionGuideData(definition);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 指标定义卡片 */}
      <Card size="small" title={
        <Space>
          <BookOutlined style={{ color: '#1890ff' }} />
          <Text strong>指标定义</Text>
        </Space>
      } style={{ background: '#f0f7ff' }}>
        <Paragraph style={{ marginBottom: 0, fontSize: '15px', lineHeight: '1.8', textAlign: 'left' }}>
          {definition.full_desc}
        </Paragraph>
      </Card>

      {/* 计算公式卡片 */}
      <Card size="small" title={
        <Space>
          <CalculatorOutlined style={{ color: '#52c41a' }} />
          <Text strong>计算公式</Text>
        </Space>
      }>
        <div className="formula-box" style={{ background: '#f6ffed', padding: '16px', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
          <code style={{ fontSize: '14px', color: '#389e0d' }}>{definition.formula}</code>
        </div>
      </Card>

      {/* 取值范围与建议 - 仅当有数据时显示 */}
      {actionGuideData.length > 0 && (
        <Card size="small" title={
          <Space>
            <LineChartOutlined style={{ color: '#fa8c16' }} />
            <Text strong>取值范围与行动建议</Text>
          </Space>
        }>
          <Timeline
            items={actionGuideData.map(item => ({
              color: item.color,
              children: (
                <div>
                  <Space align="start" size="large">
                    <Tag color={item.color} style={{ fontSize: '14px', padding: '4px 12px', minWidth: '50px', textAlign: 'center' }}>
                      {item.level}
                    </Tag>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div>
                        <Text strong style={{ fontSize: '14px', marginRight: 8 }}>{item.range}</Text>
                        <Text type="secondary">{item.desc}</Text>
                      </div>
                      <Alert
                        message="行动建议"
                        description={item.action}
                        type="info"
                        showIcon
                        style={{ marginTop: 12, background: '#f0f5ff', border: '1px solid #adc6ff' }}
                      />
                    </div>
                  </Space>
                </div>
              )
            }))}
          />
        </Card>
      )}
    </Space>
  );
};

